// Show the scoring leaders of a given league.
// This page outputs that list.

import React, { useEffect, useMemo, useState } from 'react'
import { useParams, Link } from 'react-router-dom'

import './Leaders.css'

import ErrorModal from '../../shared/components/UIElements/ErrorModal'
import HeadsUpModal from '../../shared/components/UIElements/HeadsUpModal'
import LoadingSpinner from '../../shared/components/UIElements/LoadingSpinner'

//import standingsLogo from '../../images/nsbhl-logo.png'

const Leaders = () => {
	const [error, setError] = useState()
	const [isLoading, setIsLoading] = useState(false)
	const [loadedLeaders, setLoadedLeaders] = useState(null)
	const [leagueName, setLeagueName] = useState('')
	const [session, setSession] = useState('')
	const [year, setYear] = useState('')
	const [divisionName, setDivisionName] = useState('')

	const { leagueId } = useParams()

	useEffect(() => {
		const sendRequest = async () => {
			setIsLoading(true)

			try {
				const response = await fetch(
					`${process.env.REACT_APP_BACKEND_URL}/league/${leagueId}/scoringLeaders`,
				)

				const responseData = await response.json()

				if (!response.ok) {
					throw new Error(
						responseData.message || 'Failed to load scoring leaders',
					)
				}

				const scoringLeaders = Array.isArray(responseData.scoringLeaders)
					? responseData.scoringLeaders
					: []

				setLoadedLeaders(scoringLeaders)

				setLeagueName(responseData.leagueName || '')
				setSession(responseData.session || '')
				setYear(responseData.year || '')

				setDivisionName(
					scoringLeaders[0]?.divisionName || responseData.divisionName || '',
				)
			} catch (err) {
				setError(err.message)
				setLoadedLeaders([])
				setLeagueName('')
				setSession('')
				setYear('')
				setDivisionName('')
			} finally {
				setIsLoading(false)
			}
		}

		sendRequest()
	}, [leagueId])

	const errorHandler = () => {
		setError(null)
	}

	const displayLeaders = useMemo(() => {
		if (!Array.isArray(loadedLeaders)) {
			return []
		}

		return loadedLeaders
			.map((player) => ({
				...player,
				points: Number(player.goals || 0) + Number(player.assists || 0),
			}))
			.sort((a, b) => {
				if (b.points !== a.points) {
					return b.points - a.points
				}

				return Number(b.goals || 0) - Number(a.goals || 0)
			})
			.slice(0, 10)
	}, [loadedLeaders])

	return (
		<React.Fragment>
			<HeadsUpModal error={error} onClear={errorHandler} />

			{isLoading && (
				<div className='center'>
					<LoadingSpinner />
				</div>
			)}

			{!isLoading && loadedLeaders !== null && (
				<div className='Leaders leadersSection'>
					<div className='leadersMobileCard'>
						{/* <div className='leadersMobileLogoWrapper'>
							<img
								src={standingsLogo}
								alt='National Scholastic Ball Hockey League'
								className='leadersMobileLogo'
							/>
						</div> */}

						<h1 className='leadersMainTitle'>Scoring Leaders</h1>

						<div className='leadersMobileAccent'></div>

						<h1 className='leadersLeagueTitle'>
							{leagueName}

							{divisionName && ` - ${divisionName} Division`}
						</h1>

						<h2 className='leadersSeason'>
							{session} {year}
						</h2>

						<p className='leadersInstructions'>
							(Click on a player name to see their stat history)
						</p>

						<div className='leadersTableWrapper'>
							<table id='scoringLeadersTable'>
								<thead>
									<tr>
										<th className='leaderNameHeader'>Name</th>
										<th className='leaderTeamHeader'>Team</th>
										<th>G</th>
										<th>A</th>
										<th>Pts</th>
										<th>GWG</th>
										<th>PIM</th>
									</tr>
								</thead>

								<tbody>
									{displayLeaders.map((player, key) => {
										return (
											<tr key={player.playerId || player._id || key}>
												<td className='leaderPlayerName'>
													<Link to={`/${player.playerId}/history`}>
														<span className='leaderPlayerNameText'>
															<span className='leaderPlayerNumber'>
																#{player.number ?? ''}
															</span>{' '}
															{player.firstName} {player.lastName}
														</span>
													</Link>

													<div className='leaderMobileTeamName'>
														{player.teamName}
													</div>
												</td>

												<td className='leaderTeamName'>{player.teamName}</td>

												<td>{Number(player.goals || 0)}</td>

												<td>{Number(player.assists || 0)}</td>

												<td className='pointsBold'>{player.points}</td>

												<td>{player.gwg || 0}</td>

												<td>{Number(player.pim || 0)}</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>

						{displayLeaders.length === 0 && (
							<p className='leadersEmptyMessage'>
								Scoring leaders have not been posted yet.
							</p>
						)}
					</div>
				</div>
			)}
		</React.Fragment>
	)
}

export default Leaders
